/** Client auth session — detect expired JWT and clear stale tokens. */

const TOKEN_KEYS = ["tasallot_token", "token"] as const;

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split(".")[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** True when token has a valid shape and is not past exp (30s skew). */
export function isAccessTokenValid(token: string): boolean {
  if (!token || token.split(".").length !== 3) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  const exp = payload.exp;
  if (typeof exp !== "number") return true;
  return exp * 1000 > Date.now() + 30_000;
}

export function clearAuthSession(): void {
  for (const key of TOKEN_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  window.dispatchEvent(new Event("tasallot_token_changed"));
}

/** Returns a live access token or "" after clearing expired/invalid storage. */
export function readAccessToken(): string {
  let token = "";
  try {
    token = localStorage.getItem("tasallot_token") ?? localStorage.getItem("token") ?? "";
  } catch {
    return "";
  }
  if (!token) return "";
  if (!isAccessTokenValid(token)) {
    clearAuthSession();
    return "";
  }
  return token;
}

export function loginRedirectPath(nextPath?: string): string {
  const next = nextPath ?? `${window.location.pathname}${window.location.search}`;
  return `/login?next=${encodeURIComponent(next)}`;
}

export function redirectToLogin(nextPath?: string): void {
  window.location.assign(loginRedirectPath(nextPath));
}

/** Clear session on 401 — returns true when handled. */
export function handleUnauthorizedResponse(status: number, body?: { code?: string }): boolean {
  if (status !== 401) return false;
  clearAuthSession();
  return true;
}

/** JSON headers for authenticated API calls. Omits Bearer when empty so httpOnly cookies can authenticate. */
export function buildAuthHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json", ...extra };
  const token = readAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export const AUTH_FETCH_CREDENTIALS: RequestCredentials = "include";

export function authRequiredMessage(code?: string): { dari: string; en: string; ps: string; ar: string } {
  if (code === "TOKEN_INVALID") {
    return {
      dari: "نشست شما منقضی شده — لطفاً دوباره وارد شوید",
      en: "Your session expired — please sign in again",
      ps: "ستاسو ناسته پای ته رسېدلې — بیا ننوځئ",
      ar: "انتهت جلستك — سجّل الدخول مجدداً",
    };
  }
  return {
    dari: "برای افزودن به سبد، ابتدا وارد حساب شوید",
    en: "Please sign in to add items to your cart",
    ps: "د سبد ته اضافه کولو لپاره لومړی ننوځئ",
    ar: "سجّل الدخول لإضافة المنتجات إلى السلة",
  };
}

export function parseApiError(data: unknown): { code?: string; message: string } {
  const d = data as {
    code?: string;
    message?: string;
    error?: { code?: string; message?: string };
  } | null;
  return {
    code: d?.code ?? d?.error?.code,
    message: d?.message ?? d?.error?.message ?? "",
  };
}

export function orderErrorMessage(code?: string): { dari: string; en: string; ps: string; ar: string } {
  switch (code) {
    case "VALIDATION_ERROR":
      return {
        dari: "آدرس تحویل و شهر را کامل وارد کنید",
        en: "Please enter your delivery address and city",
        ps: "د لیږد پته او ښار ولیکئ",
        ar: "أدخل عنوان التوصيل والمدينة",
      };
    case "CART_EMPTY":
      return {
        dari: "سبد خرید خالی است — محصول اضافه کنید",
        en: "Your cart is empty — add a product first",
        ps: "ګاډۍ خالي ده — لومړی توکی اضافه کړئ",
        ar: "السلة فارغة — أضف منتجاً أولاً",
      };
    case "CART_STALE":
      return {
        dari: "سبد تغییر کرد — صفحه را تازه کنید و دوباره تلاش کنید",
        en: "Cart changed — refresh and try again",
        ps: "ګاډۍ بدله شوه — پاڼه تازه کړئ",
        ar: "تغيرت السلة — حدّث الصفحة وحاول مجدداً",
      };
    case "IDEMPOTENCY_KEY_REQUIRED":
    case "IDEMPOTENCY_KEY_INVALID":
      return {
        dari: "خطای فنی — دوباره تلاش کنید",
        en: "Technical error — please try again",
        ps: "تخنیکي تېروتنه — بیا هڅه وکړئ",
        ar: "خطأ تقني — حاول مرة أخرى",
      };
    case "PAYMENT_METHOD_UNAVAILABLE":
      return {
        dari: "روش پرداخت انتخاب‌شده در دسترس نیست — روش دیگری انتخاب کنید",
        en: "Selected payment method unavailable — choose another",
        ps: "ټاکل شوې تادیې طریقه نشته — بله وټاکئ",
        ar: "طريقة الدفع غير متاحة — اختر طريقة أخرى",
      };
    default:
      return {
        dari: "خطا در ثبت سفارش — دوباره تلاش کنید",
        en: "Could not place order — please try again",
        ps: "سپارښت ناکام — بیا هڅه وکړئ",
        ar: "تعذّر تقديم الطلب — حاول مجدداً",
      };
  }
}
